import { percorsoInputSchema } from '@aulaviva/shared';
import { Alert, Button, Checkbox, TextArea, TextField, useToast } from '@aulaviva/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import type { z } from 'zod';
import { useAggiornaPercorso, useCreaPercorso, usePercorsoAdmin } from '@/api/admin';
import { ErroreRichiesta } from '@/api/client';
import { admin, comuni } from '@/testi';
import { IntestazioneAdmin, LinkIndietro } from './comuni';

type Input = z.input<typeof percorsoInputSchema>;
type Output = z.output<typeof percorsoInputSchema>;

export function PercorsoForm() {
  const { id } = useParams();
  const modifica = Boolean(id);
  const esistente = usePercorsoAdmin(id ?? '');
  if (modifica && esistente.isPending) return <p className="t-body text-ink-muted">{comuni.caricamento}</p>;
  if (modifica && esistente.isError) return <Alert variant="error" title={esistente.error.message} />;
  return <Modulo key={id ?? 'nuovo'} id={id} iniziale={esistente.data} />;
}

function Modulo({ id, iniziale }: { id?: string; iniziale?: { titolo: string; anno: number; descrizione: string | null; organizzatoreEmail: string; iscrizioneAperta: boolean } }) {
  const t = admin.percorsi.form;
  const navigate = useNavigate();
  const toast = useToast();
  const crea = useCreaPercorso();
  const aggiorna = useAggiornaPercorso(id ?? '');
  const [errore, setErrore] = useState<string | null>(null);
  const { register, control, handleSubmit, setError, formState } = useForm<Input, unknown, Output>({
    resolver: zodResolver(percorsoInputSchema),
    defaultValues: iniziale
      ? { ...iniziale, descrizione: iniziale.descrizione ?? '' }
      : { titolo: '', anno: new Date().getFullYear(), descrizione: '', organizzatoreEmail: '', iscrizioneAperta: false },
  });

  async function invia(dati: Output) {
    setErrore(null);
    try {
      const salvato = id ? await aggiorna.mutateAsync(dati) : await crea.mutateAsync(dati);
      toast.mostra({ variant: 'success', title: id ? admin.comuni.salvato : t.creato });
      navigate(`/admin/percorsi/${salvato.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ErroreRichiesta) {
        for (const [campo, m] of Object.entries(e.dettagli ?? {})) setError(campo as keyof Input, { message: m[0] });
        setErrore(e.message);
      } else setErrore(comuni.erroreGenerico);
    }
  }
  const err = formState.errors;
  return (
    <>
      <IntestazioneAdmin titolo={id ? t.titoloModifica : t.titoloNuovo} sopra={<LinkIndietro to={id ? `/admin/percorsi/${id}` : '/admin'} testo={admin.percorsi.titolo} />} />
      <form onSubmit={handleSubmit(invia)} noValidate className="flex flex-col gap-6 max-w-[640px]">
        {errore && <Alert variant="error" title={errore} />}
        <TextField label={t.titolo} error={err.titolo?.message} {...register('titolo')} />
        <TextField label={t.anno} type="number" inputMode="numeric" className="max-w-[160px]" error={err.anno?.message} {...register('anno', { valueAsNumber: true })} />
        <TextArea label={t.descrizione} hint={t.descrizioneAiuto} error={err.descrizione?.message} {...register('descrizione')} />
        <TextField label={t.organizzatoreEmail} type="email" hint={t.organizzatoreEmailAiuto} error={err.organizzatoreEmail?.message} {...register('organizzatoreEmail')} />
        <Controller
          control={control}
          name="iscrizioneAperta"
          render={({ field }) => <Checkbox checked={Boolean(field.value)} onCheckedChange={field.onChange} label={t.iscrizioneAperta} hint={t.iscrizioneApertaAiuto} />}
        />
        <div className="flex gap-3">
          <Button type="submit" variant="primary" loading={crea.isPending || aggiorna.isPending} loadingText={admin.comuni.inCorso}>
            {id ? admin.comuni.salva : t.crea}
          </Button>
          <Button onClick={() => navigate(-1)}>{admin.comuni.annulla}</Button>
        </div>
      </form>
    </>
  );
}
